import type { KonvaEventObject } from "konva/lib/Node";
import type { EditorToolKind } from "@/stores/review-editor";

/**
 * Hover-cursor affordances for the canvas editors (direct
 * manipulation). One delegated mouseover/mouseout pair on the Stage
 * (Konva events bubble) — no per-renderer handlers:
 *
 *   - empty stage / frame-catcher rects → clear the container cursor
 *     so the wrapper div's `baseCursor` applies (crosshair while a
 *     drawing tool is armed, default for select)
 *   - endpoint handles → `grab`
 *   - Transformer anchors → untouched (the Transformer manages its
 *     own per-anchor cursors)
 *   - any other node → `move` (everything is draggable now)
 *
 * While freehand is armed shapes are NOT draggable (the pen draws
 * over them), so the crosshair persists over every node.
 */
export function useStageCursors(opts: { armedTool: EditorToolKind }) {
  const { armedTool } = opts;
  const baseCursor: "default" | "crosshair" =
    armedTool === "select" ? "default" : "crosshair";

  const onMouseOver = (e: KonvaEventObject<MouseEvent>) => {
    const stage = e.target.getStage();
    if (!stage) return;
    const container = stage.container();
    if (
      e.target === (stage as unknown) ||
      armedTool === "freehand" ||
      e.target.name() === "frame-catcher"
    ) {
      container.style.cursor = "";
      return;
    }
    if (e.target.getParent()?.className === "Transformer") return;
    if (e.target.findAncestor(".endpoint-handle", true)) {
      container.style.cursor = "grab";
      return;
    }
    container.style.cursor = "move";
  };

  const onMouseOut = (e: KonvaEventObject<MouseEvent>) => {
    const stage = e.target.getStage();
    if (!stage) return;
    if (e.target === (stage as unknown)) return;
    stage.container().style.cursor = "";
  };

  return { baseCursor, onMouseOver, onMouseOut };
}
